import React, { useEffect, useState, useRef } from 'react';
import { Container, Button } from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom'
import 'react-html5-camera-photo/build/css/index.css';
import Webcam from 'react-webcam';
import Loading from '../Loading/Loading';
import useResource from '../../api/services/useResource';
import useCreateResource from '../../api/services/useCreateResource';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import { confirmAlert } from '../ui/ConfirmAlert';
import usePageTitle from '../../hooks/usePageTitle';
import { queryClient } from '../../api/queryClient';

const videoConstraints = {
  width: 720,
  height: 720,
  facingMode: 'environment',
};

function TakePhoto() {
  usePageTitle('BGL - Ingresar Bien');
  const { numerointerno } = useParams();
  const navigate = useNavigate();
  const { mutate: uploadImage, isLoading: isUploadingImage } =
    useCreateResource({
      resource: '/item/image',
    });
  const { data: itemData, isLoading } = useResource({
    resource: '/item/form',
    params: { numerointerno },
  });
  const webcamRef = useRef(null);
  const [imagesGallery, setImageGallery] = useState([]);
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);

  const clearCurrentPhoto = () => {
    setCurrentPhoto(null);
  };

  const handleCameraError = (error) => {
    console.error('Webcam error:', error);

    // Check for permission errors
    if (
      error.name === 'NotAllowedError' ||
      error.name === 'PermissionDeniedError'
    ) {
      setCameraError('permission');
    } else if (
      error.name === 'NotFoundError' ||
      error.name === 'DevicesNotFoundError'
    ) {
      setCameraError('notfound');
    } else if (
      error.name === 'NotReadableError' ||
      error.name === 'TrackStartError'
    ) {
      setCameraError('inuse');
    } else {
      setCameraError('generic');
    }
  };

  const handleCameraReady = () => {
    setCameraReady(true);
    setCameraError(null);
  };

  const retryCamera = () => {
    setCameraError(null);
    setCameraReady(false);
  };

  const capture = () => {
    try {
      if (!webcamRef.current) {
        console.error('Webcam ref is not available');
        return;
      }

      const imageSrc = webcamRef.current.getScreenshot();

      if (!imageSrc) {
        console.error('Failed to capture screenshot');
        return;
      }

      setCurrentPhoto({ web: true, data: imageSrc });
    } catch (error) {
      console.error('Error capturing photo:', error);
    }
  };

  //TODO: guardar Imagenes con acta True
  const saveImage = () => {
    if (currentPhoto?.data) {
      uploadImage(
        { data: { numerointerno, acta: true, images: [currentPhoto.data] } },
        {
          onSuccess: (resData) => {
            if (resData.urls && resData.urls.length > 0) {
              setImageGallery((state) => [...state, resData.urls[0]]);
              clearCurrentPhoto();

              // Invalidate and refetch the item data to get updated images
              queryClient.invalidateQueries({
                queryKey: ['/item/form', '', { numerointerno }],
              });
            } else {
              console.error('No URLs returned from server:', resData);
            }
          },
          onError: (error) => {
            console.error('Upload error:', error);
          },
        },
      );
    }
  };

  const handleFinish = () => {
    confirmAlert({
      title: 'Terminar toma de fotos',
      question:
        '¿Estas seguro que quieres terminar la toma de fotos y guardar el Bien?',
      confirmText: 'Guardar',
      onConfirm: () => navigate('/create-items'),
    });
  };

  useEffect(() => {
    if (itemData?.data?.data?.img) {
      const mappedImages = itemData.data.data.img.map((img) => img?.data);
      setImageGallery(mappedImages);
    }
  }, [itemData]);

  const renderCameraError = () => {
    const errorMessages = {
      permission: {
        title: 'Permisos de cámara denegados',
        message: 'Para tomar fotos, necesitas permitir el acceso a la cámara.',
        instructions: [
          '1. Haz clic en el ícono de cámara en la barra de direcciones',
          '2. Selecciona "Permitir" para el acceso a la cámara',
          '3. Recarga la página si es necesario',
        ],
      },
      notfound: {
        title: 'Cámara no encontrada',
        message: 'No se detectó ninguna cámara en tu dispositivo.',
        instructions: [
          '1. Verifica que tu dispositivo tenga una cámara',
          '2. Asegúrate de que esté conectada correctamente',
          '3. Reinicia el navegador si es necesario',
        ],
      },
      inuse: {
        title: 'Cámara en uso',
        message: 'La cámara está siendo utilizada por otra aplicación.',
        instructions: [
          '1. Cierra otras aplicaciones que puedan estar usando la cámara',
          '2. Reinicia el navegador',
          '3. Intenta de nuevo',
        ],
      },
      generic: {
        title: 'Error de cámara',
        message: 'Hubo un problema al acceder a la cámara.',
        instructions: [
          '1. Verifica los permisos de cámara',
          '2. Reinicia el navegador',
          '3. Intenta de nuevo',
        ],
      },
    };

    const error = errorMessages[cameraError];

    return (
      <div className="alert alert-danger">
        <h4 className="alert-heading">
          <i className="fa fa-camera-retro mr-2"></i>
          {error.title}
        </h4>
        <p className="mb-3">{error.message}</p>
        <div className="mb-3">
          <strong>Pasos a seguir:</strong>
          <ul className="mt-2 mb-0">
            {error.instructions.map((instruction, index) => (
              <li key={index}>{instruction}</li>
            ))}
          </ul>
        </div>
        <Button color="primary" onClick={retryCamera}>
          <i className="fa fa-refresh mr-1"></i>
          Intentar de nuevo
        </Button>
      </div>
    );
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Container>
      {!currentPhoto?.data ? (
        <div>
          {cameraError ? (
            renderCameraError()
          ) : (
            <>
              <div className="d-flex justify-content-center">
                <Webcam
                  audio={false}
                  height={720}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  width={720}
                  mirrored={false}
                  videoConstraints={videoConstraints}
                  onUserMedia={handleCameraReady}
                  onUserMediaError={handleCameraError}
                />
              </div>
              <div className="d-flex justify-content-center">
                <button
                  className="btn btn-success w-50"
                  onClick={capture}
                  disabled={!cameraReady}
                >
                  {cameraReady ? 'Tomar Foto' : 'Inicializando cámara...'}
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div>
          <div className="d-flex justify-content-center">
            {currentPhoto?.data && (
              <img src={currentPhoto.data} alt="sin imagen" />
            )}
          </div>
          <div className="d-flex justify-content-between mt-2">
            <Button
              className="btn btn-success"
              onClick={saveImage}
              disabled={isUploadingImage}
            >
              Guardar Imagen
            </Button>
            <Button
              className="btn btn-danger"
              onClick={clearCurrentPhoto}
              disabled={isUploadingImage}
            >
              Volver a tomar
            </Button>
          </div>
        </div>
      )}

      <div>
        <h2>Galeria</h2>
        <ImageGallery
          items={
            imagesGallery?.map((src) => {
              return {
                original: src,
                originalHeight: 500,
                originalWidth: 500,
                thumbnail: src,
              };
            }) || []
          }
          infinite={false}
          autoPlay={false}
          showPlayButton={false}
          showFullscreenButton={false}
        />
      </div>
      <Button className={'btn btn-danger w-100 mt-2'} onClick={handleFinish}>
        Terminar
      </Button>
    </Container>
  );
}

export default TakePhoto;
